import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'

import { diggingAjax } from '../ajax/DiggingAjax'
import { store } from '../state/store'
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO'
import { appendCachedDiggingSummaryRow } from './patchCachedDiggingSummary'
import { computeShiftId } from './computeShiftId'
import { enqueue } from './queueOps'
import { nextTempId } from './tempId'
import type { TripleShift } from '../types/Shift'
import { isOnlineNow } from './connectivity'
import { mayQueueOffline, offlineRefusal } from './offlineWrites'
import { sendDirect } from './directSave'
import { formatTimeOnly } from '../utilities/TimeOnly'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

export const diggingPatternMutations = {
  async insertDiggingLocation(
    shiftDate: DateTime,
    dayOrNight: TripleShift,
    diggerId: number,
    location: DiggingLocationDTO,
    shiftIdOverride?: number,
  ) {
    const clientId = uuidv4()
    const sentDirect = isOnlineNow()
    if (sentDirect) {
      const sent = await sendDirect(() => diggingAjax.insertDiggingLocation(shiftDate, dayOrNight, diggerId, location, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()
    const tempId = uuidv4()
    const tempDiggingPatternId = await nextTempId()
    await enqueue({
      clientId,
      sentDirect,
      entity: 'diggingPattern',
      operation: 'create',
      shiftId: shiftIdOverride ?? currentShiftId(),
      tempId,
      entityId: tempDiggingPatternId,
      data: {
        diggerId,
        patternBlastId: location.patternBlastId,
        startTime: formatTimeOnly(location.startTime),
        blastMasterId: location.blastMasterId,
        pitStageId: location.pitStageId,
      },
    })
    const created = new DiggingLocationDTO({ ...location, diggingPatternId: tempDiggingPatternId })
    appendCachedDiggingSummaryRow(created, shiftDate, dayOrNight, diggerId)

    return { success: true, data: created as DiggingLocationDTO | null }
  },

  async updateDiggingLocation(location: DiggingLocationDTO) {
    const clientId = uuidv4()
    if (isOnlineNow()) {
      const sent = await sendDirect(() => diggingAjax.updateDiggingLocation(location, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()
    await enqueue({
      clientId,
      entity: 'diggingPattern',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: location.diggingPatternId,
      rowVersion: location.rowVersion,
      data: {
        patternBlastId: location.patternBlastId,
        startTime: formatTimeOnly(location.startTime),
        blastMasterId: location.blastMasterId,
        pitStageId: location.pitStageId,
      },
    })
    return { success: true, data: location as DiggingLocationDTO | null }
  },

  async deleteDiggingPattern(diggingPatternId: number, rowVersion?: string) {
    const clientId = uuidv4()
    if (isOnlineNow()) {
      const sent = await sendDirect(() => diggingAjax.deleteDiggingPattern(diggingPatternId, clientId))
      if (sent) return sent
    }
    if (!mayQueueOffline()) return offlineRefusal()
    await enqueue({
      clientId,
      entity: 'diggingPattern',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: diggingPatternId,
      rowVersion,
    })
    return { success: true, data: null as null }
  },
}
